import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { AbstractControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SHARED_COMPONENTS } from '../../../../../shared/shared-imports';
import { LookupKey } from '../../../../../shared/constants/lookup-keys';
import { debounceTime, distinctUntilChanged, filter, finalize, merge, Subject, Subscription, switchMap, tap } from 'rxjs';
import { Utils } from '../../../../../Utils/utils';
import { LanguageService } from '../../../../../core/services/language.service';
import { DocumentServiceType, FieldValue, SrActionType } from '../../../../../shared/constants/services.enum';
import { OptionFieldComponent } from '../../../../../shared/components/controls/option-field/option-field.component';
import { TextFieldComponent } from '../../../../../shared/components/controls/text-field/text-field.component';
import { SearchableListComponent } from '../../../../../shared/components/controls/searchable-list/searchable-list.component';
import { MobileNumberComponent } from '../../../../../shared/components/controls/mobile-number/mobile-number.component';
import { DateComponent } from '../../../../../shared/components/controls/date/date.component';
import { SharedService } from '../../../../../shared/services/shared.service';
import { AllowedDocumentType } from '../../../../../shared/models/documents-response.model';
import { FileComponent } from '../../../../../shared/components/controls/file/file.component';
import { AddCompanyComponent } from "../add-company/add-company.component";
import { NgClass } from '@angular/common';
import { LookupService } from '../../../../../core/services/lookup.service';

@Component({
  selector: 'app-defaulter-company-detail',
  imports: [ReactiveFormsModule, TranslateModule, ...SHARED_COMPONENTS, AddCompanyComponent, NgClass],
  templateUrl: './defaulter-company-detail.component.html',
  styleUrl: './defaulter-company-detail.component.scss'
})
export class DefaulterCompanyDetailComponent implements OnInit, OnChanges, OnDestroy {
  documentServiceType = DocumentServiceType;
  @ViewChildren(TextFieldComponent) textFields!: QueryList<TextFieldComponent>;
  @ViewChildren(OptionFieldComponent) optionFields!: QueryList<OptionFieldComponent>;
  @ViewChildren(SearchableListComponent) searchOptionFields!: QueryList<SearchableListComponent>;
  @ViewChildren(MobileNumberComponent) mobileFields!: QueryList<MobileNumberComponent>;
  @ViewChildren(DateComponent) dateFields!: QueryList<DateComponent>;
  @ViewChildren('fileCmp') fileComponents!: QueryList<FileComponent>;

  allFields: any[] = [];
  @Input() currencyList: any;
  @Input() defaulterCompany: any;
  @Input() showActions: boolean = true;
  @Input() srDetails: any;
  @Input() folder: any = "";
  @Input() form: any = "";
  @Input() mainFromSectionDocs: AllowedDocumentType[] = [];
  @Output() onSubmit = new EventEmitter();
  @Output() onClickAddEditCompany = new EventEmitter();
  @Input() currentStep: any;
  @Input() DCMember: boolean = false;
  lookupKey = LookupKey;
  lang: any = 'en';
  isDisableCompForm: boolean = false;
  // This method is used to initialize the class instance using language service and shared service
  constructor(
    private languageService: LanguageService,
    private sharedService: SharedService,
    private lookupService: LookupService
  ) {
    this.lang = this.languageService.getCurrentLang();
  }

  members: any = [];
  private preload: any[] = []; // first 20
  private search$ = new Subject<string>();
  loadingMember: boolean = false;
  private dateRangeSubscription?: Subscription;

  // This method is used to handle the on init lifecycle hook
  ngOnInit(): void {
    // Ensure page starts at top when this component loads
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
    //Search for defaulter company/member
    this.search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((term: any) => {
          // ≤2 chars or cleared ⇒ just show preload
          if (term?.searchText && term?.searchText.length < 1) {
            this.members = this.preload;
          }
        }),
        filter((term) => term?.searchText && term?.searchText.length >= 1),
        tap(() => (this.loadingMember = true, this.members = [])),
        switchMap((term: any) =>
          this.getMembers(term.searchText, term.searchBy)

        )
      )
      .subscribe((response: any) => {
        if (response && response['Error Code'] == "" && response['SiebelMessage']["DC Account Thin"]) {
          let memberData = Utils.checkIsArray(response['SiebelMessage']["DC Account Thin"]);
          this.members = memberData.map((item: any) => ({
            ...item,
            name: item["DC Name English"],
            description: item["DC Name Arabic"],
            memberNumber: item["CSN"] || item["Account Number"],
            licenseNumber: item["DC License Registration Number"]
          }));

          this.loadingMember = false;
          this.preload = this.members;
        } else {
          this.loadingMember = false;
          this.members = [];
        }
      })
    this.setupDateRangeValidation();
  }

  // This method is used to handle the on changes lifecycle hook using changes
  ngOnChanges(changes: SimpleChanges): void {
    this.getCountryList();
    if (changes['form']?.currentValue) {
      this.setupDateRangeValidation();
    }
    if (!this.showActions) {
      this.isDisableCompForm = true;
      this.form.disable();
    }
    if (this.defaulterCompany) {
      this.form.get('company')?.setValue(this.defaulterCompany);
      this.form.get('company')?.updateValueAndValidity();
      this.updateCompanyContactError(this.defaulterCompany);
    }
    if (this.srDetails) {
      const fromDate = this.srDetails["DC Transaction From Date"]
        ? new Date(this.srDetails["DC Transaction From Date"])
        : "";
      const toDate = this.srDetails["DC Transaction To Date"]
        ? new Date(this.srDetails["DC Transaction To Date"])
        : "";

      this.form.patchValue({
        company: this.defaulterCompany,
        pocFullName: this.srDetails["DC Person Incharge"],
        pocMobile: this.srDetails["DC Person Incharge Mobile"] ? Utils.parseMobile(this.srDetails["DC Person Incharge Mobile"], 'number') : "",
        pocEmail: this.srDetails["DC Person Incharge Email Address"],
        pocPhone: this.srDetails["DC Person Incharge Telephone Number"] ? Utils.parseMobile(this.srDetails["DC Person Incharge Telephone Number"], 'number') : "",
        pocCountry: this.srDetails["DC Person Incharge Country"],
        pocPhoneCC: this.srDetails["DC Person Incharge Telephone Number"] ? Utils.parseMobile(this.srDetails["DC Person Incharge Telephone Number"], 'cc') : "",
        pocMobileCC: this.srDetails["DC Person Incharge Mobile"] ? Utils.parseMobile(this.srDetails["DC Person Incharge Mobile"], 'cc') : "",
        pocCity: this.srDetails["DC Person Incharge City"],
        type: this.srDetails["DC Nature Of Dispute"],
        details: this.srDetails["Description"], //this.srDetails["DC Dispute In Brief Flag"],
        currency: this.srDetails["Currency"] ? this.srDetails["Currency"] : "AED",
        amount: this.srDetails["DC Invoice Amount"],
        dateMode: fromDate && toDate ? true : false,
        dateFrom: {
          day: fromDate ? fromDate.getDate().toString().padStart(2, '0') : null,
          month: fromDate ? Number(fromDate.getMonth() + 1).toString().padStart(2, '0') : null,
          year: fromDate ? fromDate.getFullYear() : null,
        },
        dateTo: {
          day: toDate ? toDate.getDate().toString().padStart(2, '0') : null,
          month: toDate ? Number(toDate.getMonth() + 1).toString().padStart(2, '0') : null,
          year: toDate ? toDate.getFullYear() : null,
        },
        request: this.srDetails["DC Complainant Request"],
        paymentMethod: this.srDetails["DC Payment Method"] ? this.srDetails["DC Payment Method"] : null,
      })
      this.currentStep = true;
      this.form.get("currency")?.disable();
      
      this.onPocCountryChange(this.srDetails["DC Person Incharge Country"]);
    }
  }
  countryList: any = [];
  getCountryList() {
    this.lookupService.post<any>(LookupKey.LOOKUP_URL, {
      ProcessName: 'DC Get List Of Values WF',
      Type: LookupKey.COUNTRY,
      ParentValue: ''
    }).subscribe({
      next: (response: any) => {
        this.countryList = Utils.checkIsArray(response?.SiebelMessage?.['List Of Values']);
      }
    })
  }
  getCountryValue(country: string) {
    if (this.countryList && this.countryList.length > 0) {
      let countryObj = this.countryList.find((c: any) => c.Value == country || c.Name == country);
      return countryObj ? countryObj.Value : country;
    }
  }
    // This method is used to add new company
    addNewCompany() {
      let object = { data: "" }
      this.onClickAddEditCompany.emit(object)
    }

    // This method is used to set company selected using data
    setCompanySelected(data: any) {
      let cutAddress: any;
      if (data[0]?.["CUT Address"]) {
        let addr = Utils.checkIsArray(data[0]?.["CUT Address"]);
        cutAddress = addr ? addr[0] : "";
      }

      let memberAddress = "";
      if (cutAddress) {
        // ${cutAddress["Descriptor"] || ""}
        memberAddress = `${cutAddress["DC Address English Name"] || ""} ${cutAddress["Street Address"] || ""}`.trim();
      }
      //strict max length of 150
      memberAddress = memberAddress.substring(0, 150);

      let selectedCompany = {
        respondentMemberNumber: data[0]["CSN"],
        nameEnglish: data[0]["DC Name English"],
        nameArabic: data[0]["DC Name Arabic"],
        country: cutAddress ? this.getCountryValue(cutAddress["Country"]) : "",
        email: data[0]["DC CRB Primary Email"] ? data[0]["DC CRB Primary Email"] : '',
        phone: data[0]["DC Holder Phone"] ? Utils.parseMobile(data[0]["DC Holder Phone"], 'number') : "",
        countryCode: data[0]["DC Holder Phone"] ? Utils.parseMobile(data[0]["DC Holder Phone"], 'cc') : "",
        city: cutAddress ? cutAddress["City"] : "",
        address: cutAddress ? memberAddress : "",
        poBox: "",
      }
      this.defaulterCompany = selectedCompany;
      this.form.get('company')?.setValue(this.defaulterCompany);
      this.form.get('company')?.updateValueAndValidity();
      this.updateCompanyContactError(this.defaulterCompany);
    }

    // This method is used to edit detail using company detail
    editDetail(companyDetail: any) {
      this.defaulterCompany = companyDetail;
      this.onClickAddEditCompany.emit({ data: companyDetail })
    }

    // This method is used to remove company
    removeCompany() {
      this.defaulterCompany = "";
      this.form.get('company')?.setValue("");
      this.form.get('company')?.updateValueAndValidity();
      this.form.markAllAsTouched();
      this.companyContactError = '';
    }

    /* receives the search term from the child */
    // This method is used to handle search using term
    handleSearch(term: string) {
      this.search$.next(term);
    }

    // This method is used to get members using term and search by
    getMembers(term: string = "", searchBy: string = "") {
      return this.sharedService.getDCAllMembers(term, searchBy);
    }

    // This method is used to save and close
    saveAndClose() {
      let data = {
        formData: this.form.getRawValue(),
        actionType: SrActionType.SAVE
      }
      this.onSubmit.emit(data);
    }
    formSubmitLoader: boolean = false;
    companyContactError: string = '';
  private readonly requiredCompanyContactFields: Array<'email' | 'phone' | 'country'> = ['email', 'phone', 'country'];
  private readonly companyContactFieldLabelMap: Record<'email' | 'phone' | 'country' | 'address', string> = {
    email: 'Email',
    phone: 'Phone',
    country: 'Country',
    address: 'Address'
  };

  // This method is used to handle the on destroy lifecycle hook
  ngOnDestroy(): void {
    this.dateRangeSubscription?.unsubscribe();
  }

  // This method is used to set up date range validation
  private setupDateRangeValidation(): void {
    if (!this.form) {
      return;
    }

    const dateFromControl = this.form.get('dateFrom');
    const dateToControl = this.form.get('dateTo');

    if (!(dateFromControl instanceof FormGroup) || !(dateToControl instanceof FormGroup)) {
      return;
    }

    const dateModeControl = this.form.get('dateMode');
    // This method is used to validate range
    const validateRange = () => this.applyDateRangeValidation(dateFromControl, dateToControl, dateModeControl);

    this.dateRangeSubscription?.unsubscribe();

    const streams = [dateFromControl.valueChanges, dateToControl.valueChanges];
    if (dateModeControl) {
      streams.push(dateModeControl.valueChanges);
    }

    this.dateRangeSubscription = merge(...streams).subscribe(validateRange);
    validateRange();
  }

  // This method is used to apply date range validation using date from group, date to group, and date mode control
  private applyDateRangeValidation(dateFromGroup: FormGroup, dateToGroup: FormGroup, dateModeControl?: AbstractControl | null): void {
    if (dateToGroup.disabled) {
      this.updateDateToErrors(dateToGroup, {});
      return;
    }

    const isRangeActive = dateModeControl ? !!dateModeControl.value : true;
    if (!isRangeActive) {
      this.updateDateToErrors(dateToGroup, {});
      return;
    }

    const fromDate = this.extractDateValue(dateFromGroup);
    const toDate = this.extractDateValue(dateToGroup);
    const customErrors: ValidationErrors = {};

    if (fromDate && toDate && toDate <= fromDate) {
      customErrors['dateOrder'] = true;
    }

    if (toDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (toDate >= today) {
        customErrors['dateFuture'] = true;
      }
    }

    this.updateDateToErrors(dateToGroup, customErrors);
  }

  // This method is used to extract date value using control
  private extractDateValue(control: FormGroup | AbstractControl | null): Date | null {
    if (!(control instanceof FormGroup)) {
      return null;
    }

    const day = Number(control.get('day')?.value);
    const month = Number(control.get('month')?.value);
    const year = Number(control.get('year')?.value);

    if (!day || !month || !year) {
      return null;
    }

    const date = new Date(year, month - 1, day);
    return isNaN(date.getTime()) ? null : date;
  }

  // This method is used to update date to errors using control and errors to add
  private updateDateToErrors(control: FormGroup | null, errorsToAdd: ValidationErrors): void {
    if (!control) {
      return;
    }

    const currentErrors = control.errors || {};
    const preservedErrors = { ...currentErrors };
    delete preservedErrors['dateOrder'];
    delete preservedErrors['dateFuture'];

    const combinedErrors = { ...preservedErrors, ...errorsToAdd };
    const currentKeys = Object.keys(currentErrors);
    const newKeys = Object.keys(combinedErrors);
    const keysChanged = currentKeys.length !== newKeys.length || currentKeys.some((key) => !newKeys.includes(key));
    const valuesChanged = newKeys.some((key) => currentErrors[key] !== combinedErrors[key]);

    if (newKeys.length === 0) {
      if (keysChanged) {
        control.setErrors(null);
        control.updateValueAndValidity({ emitEvent: false });
      }
      return;
    }

    if (keysChanged || valuesChanged) {
      control.setErrors(combinedErrors);
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  // This method is used to collect field components
  private collectFieldComponents(): void {
    const groups: QueryList<any>[] = [];

    if (this.textFields) {
      groups.push(this.textFields);
    }
    if (this.optionFields) {
      groups.push(this.optionFields);
    }
    if (this.searchOptionFields) {
      groups.push(this.searchOptionFields);
    }
    if (this.mobileFields) {
      groups.push(this.mobileFields);
    }
    if (this.dateFields) {
      groups.push(this.dateFields);
    }

    this.allFields = groups.reduce(
      (acc: any[], group) => acc.concat(group.toArray()),
      []
    );
  }

  // This method is used to check invalid fields
  checkInvalidFields(): boolean {
    this.collectFieldComponents();
    const fieldsToCheck = [
      'company',
      'pocFullName',
      'pocMobile',
      'pocEmail',
      'pocMobileCC',
      'pocPhone',
      'pocCountry',
      'pocCity',
      'type',
      'details',
      'currency',
      'amount',
      'dateFrom',
      'dateTo',
      'request'
    ];

    if (Utils.handleInvalidStep(this.form, this.allFields, fieldsToCheck)) {
      const invalidControls = Utils.getInvalidControls(this.form);
      this.form.markAllAsTouched();
    }

    Utils.focusFirstInvalidControl(this.form, this.allFields);
    this.form.markAllAsTouched();

    return this.form.invalid; // true if invalid, false if valid
  }
  // This method is used to move to the next step
  nextStep() {
    this.collectFieldComponents();

    if (this.form.invalid) {
      Utils.focusFirstInvalidControl(this.form, this.allFields);
      this.form.markAllAsTouched();
      return;
    }

    if (!this.currentStep) {
      const data = {
        formData: this.form.getRawValue(),
        actionType: SrActionType.DRAFT
      };
      this.onSubmit.emit(data);
      this.currentStep = true;
    } else {
      if (this.updateCompanyContactError(this.defaulterCompany)) {
        return;
      }
      if (this.checkRequiredDocuments()) {
        return;
      }

      this.formSubmitLoader = true;
      const data = {
        formData: this.form.getRawValue(),
        actionType: SrActionType.SUBMIT
      };
      this.onSubmit.emit(data);
    }
  }

  private updateCompanyContactError(company: any): boolean {
    if (!company) {
      this.companyContactError = '';
      return false;
    }

    const missingFields = this.requiredCompanyContactFields.filter((field) => {
      const value = company[field];
      if (typeof value === 'string') {
        return value.trim() === '';
      }
      return !value;
    });

    if (missingFields.length) {
      const readableFields = missingFields.map((field) => this.companyContactFieldLabelMap[field]).join(', ');
      // this.companyContactError = `Missing company details: ${readableFields}. Please edit the selected company before submitting.`;
      this.companyContactError = readableFields;
      return true;
    }

    this.companyContactError = '';
    return false;
  }

  // This method is used to check required documents
  checkRequiredDocuments(): boolean {
    // If there are no docs to check, nothing is missing
    if (!this.mainFromSectionDocs || this.mainFromSectionDocs.length === 0) {
      return false;
    }
    const fileCmps = this.fileComponents && typeof this.fileComponents.toArray === 'function'
      ? this.fileComponents.toArray()
      : [];

    // ViewChildren not ready or not fully rendered yet – defer document checks
    if (fileCmps.length < this.mainFromSectionDocs.length) {
      return false;
    }

    let hasMissing = false;
    for (let index = 0; index < this.mainFromSectionDocs.length; index++) {
      const doc = this.mainFromSectionDocs[index];
      const cmp = fileCmps[index];
      // Safety guard; should not happen due to length check above
      if (!cmp) continue;

      if (typeof cmp.checkMissing === 'function' && cmp.checkMissing((doc)?.mandatory)) {
        hasMissing = true;
      }
    }
    return hasMissing;
  }
  addCompanyModal: boolean = false;
  // This method is used to submit company deatil using company form data
  submitCompanyDeatil(companyFormData: any) {
    this.defaulterCompany = companyFormData;
    this.form.get('company')?.setValue(this.defaulterCompany);
    this.form.get('company')?.updateValueAndValidity();
    this.updateCompanyContactError(this.defaulterCompany);
  }

  // method for updae attachment
  // This method is used to handel file event using document and document field
  handelFileEvent(document: any, documentField: any) {
    let documents = Utils.checkIsArray(document);
    Utils.setControlValidityBasedOnDocument(this.form, documentField, documents);
  }

  // This method is used to handle date range change event using event
  onDateRangeChange(event: any) {
    const dateToGroup = this.form.get('dateTo') as FormGroup;
    if (!event) {
      //Disable and clear validators
      this.dateErrorMsg = '';
      dateToGroup.disable();
      Object.keys(dateToGroup.controls).forEach(controlName => {
        const control = dateToGroup.get(controlName);
        control?.clearValidators();
        control?.updateValueAndValidity({ emitEvent: false });
      });
      dateToGroup.reset();
    } else {
      // Enable and set required validators
      dateToGroup.enable();
      Object.keys(dateToGroup.controls).forEach(controlName => {
        const control = dateToGroup.get(controlName);
        control?.setValidators(Validators.required);
        control?.updateValueAndValidity({ emitEvent: false });
      });
    }
  }
  dateErrorMsg: string = '';
  // This method is used to validate date range
  validateDateRange() {
    this.dateErrorMsg = '';
    const fromGroup = this.form.get('dateFrom') as FormGroup;
    const toGroup = this.form.get('dateTo') as FormGroup;

    if (!fromGroup || !toGroup) return;

    const { day: fromDay, month: fromMonth, year: fromYear } = fromGroup.value;
    const { day: toDay, month: toMonth, year: toYear } = toGroup.value;
    // if dateTo is empty, skip validation
    if (!toDay || !toMonth || !toYear) {
      this.dateErrorMsg = "";
      return;
    }
    // build JS Date objects (note: month is 0-based in JS Date)
    const fromDate = new Date(fromYear, fromMonth - 1, fromDay);
    const toDate = new Date(toYear, toMonth - 1, toDay);

    // normalize
    fromDate.setHours(0, 0, 0, 0);
    toDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (toDate < fromDate) {
      this.form.get('dateTo')?.patchValue({ day: null, month: null, year: null });
      this.dateErrorMsg = "ALERT.DATE_TO_GREATER_ERROR";
      return;
    }

    if (toDate > today) {
      this.form.get('dateTo')?.patchValue({ day: null, month: null, year: null });
      this.dateErrorMsg = "ALERT.DATE_TO_LESS_ERROR";
      return;
    }
  }
  // This method is used to handle payment method change using option
  noAdvancePaymentBalance: boolean = false;
  onPaymentMethodChange(value: any) {
    this.noAdvancePaymentBalance = false;
    const selectedValue = value;
    if (selectedValue === FieldValue.PAYMENT_METHOD_ADVANCE_FOR_SERVICES || selectedValue === FieldValue.PAYMENT_METHOD_ADVANCE_FOR_SERVICES_AR) {
      this.noAdvancePaymentBalance = !this.sharedService.hasSufficientAdvanceForServicesBalance();
    } else {
      this.noAdvancePaymentBalance = false;
    }
  }
  // This method is used to check selected county based on it disabled city
  onPocCountryChange(value: any) {
    if (!this.form) {
      return;
    }
    const pocCountryControl = this.form.get('pocCountry');
    const pocCityControl = this.form.get('pocCity');
    if (!pocCountryControl?.value) {
      return;
    }

    if (pocCountryControl?.value != FieldValue.UAE && pocCountryControl?.value != FieldValue.UAE_AR) {
      pocCityControl.setValue('N/A');
      pocCityControl.disable();
    } else {
      if (pocCountryControl?.value == FieldValue.UAE || pocCountryControl?.value == FieldValue.UAE_AR) {
        pocCityControl.enable();
        if (pocCityControl?.value == "N/A") {
          pocCityControl.setValue(null);
        }
      }
    }
  }
}
